// import { useFlag } from '@unleash/proxy-client-react'
import React, { lazy, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { RouteConfig } from '@app/routing/RenderRoutes'

import { Navigate } from '@lib/react-router-dom'

import { DashboardWrapper } from '@pages/IntroducingBroker/DashboardPoC/DashboardWrapper'
import { IBDashboardEditPage } from '@pages/IntroducingBroker/DashboardPoC/Edit/IBDashboardEdit.page'
import { IBDashboardPage } from '@pages/IntroducingBroker/DashboardPoC/IBDashboardPage'
import { LinkHubPage } from '@pages/IntroducingBroker/LinkHub/LinkHubPage'
import { IBQuestionnairePageGuard } from '@pages/IntroducingBroker/SignUp/Questionnaire/IBQuestionnairePageGuard'
import { IBSignupThankYouPageGuard } from '@pages/IntroducingBroker/SignUp/ThankYou/IBSignupThankYouPageGuard'
import { WithdrawalAmountPage } from '@pages/Traders-Room/Withdrawal/Amount/WithdrawalAmount.page'
import { WithdrawalTransactionPage } from '@pages/Traders-Room/Withdrawal/Transaction/WithdrawalTransaction.page'
import { WithdrawalTwoFactorPage } from '@pages/Traders-Room/Withdrawal/TwoFactor/WithdrawalTwoFactor.page'
import { WithdrawalPage } from '@pages/Traders-Room/Withdrawal/Withdrawal.page'
import { WithdrawalFlowLayoutPage } from '@pages/Traders-Room/Withdrawal/WithdrawalFlowLayout/WithdrawalFlowLayout.page'

import { useAccountReadContext } from '@context/AccountContextContext'
import { DateFilterContextProvider } from '@context/DateFilterContext'

import { isTickmillAS, isTickmillPartner } from '@utils/companyName.utils'
import { CommonPaths } from '@utils/path'

import { useIBPrograms } from '@hooks/useIBPrograms'

import { IBContestPage } from './Contest/IBContestPage'
import { IntroducingBroker } from './IntroducingBroker'
import { LegalDocumentsPage } from './LegalDocuments/LegalDocumentsPage'
import { LoyaltyPage } from './Loyalty/LoyaltyPage'
import { CommissionPlansPage } from './MultiTier/CommissionPlans/CommissionPlansPage'
import { MultiTierNetworkPage } from './MultiTier/Network/MultiTierNetworkPage'
import { SubIBAccountsPage } from './MultiTier/SubIBAccounts/SubIBAccountsPage'
import { IBRebateApprovalsPage } from './Rebates/Approvals/Approvals.page'
import { IBRebatesClientsPage } from './Rebates/Clients/Clients.page'
import { IBRebatesGroupsPage } from './Rebates/Groups/Groups.page'
import { IBRebatesPage } from './Rebates/RebatesPage'
import { IBRebatesTransactionsPage } from './Rebates/Transactions/Transactions.page'
import { ReferralMaterialsPage } from './Referrals/ReferralMaterialsPage'
import { IBClientJourneyMapPage } from './Reports/ClientJourneyMap/IBClientJourneyMap'
import { IBClientsPage } from './Reports/Clients/IBClientsPage'
import { ExportHubPage } from './Reports/ExportHub/ExportHubPage'
import { IBIncomePage } from './Reports/Income/IBIncomePage'
import { IBReportsPage } from './Reports/Reports/IBReportsPage'
import { ReportsPage } from './Reports/ReportsPage'
import { IBTransactionHistoryPage } from './Reports/TransactionsHistory/IBTransactionHistoryPage'
import { IBSignUpPage } from './SignUp/IBSignUpPage'
import { IBSignUpTypePage } from './SignUp/Type/IBSignUpTypePage'
import { IBWalletTransferPage } from './Wallets/IBWalletTransfer.page'
import { IBWalletsPage } from './Wallets/IBWallets.page'
import { useIntroducingBrokerById } from './hooks/useIntroducingBrokerById'

const MultiTierPage = lazy(() => import('./MultiTier/MultiTierPage'))

export const IB_DEFAULT_REGISTER_PAGE_PATH = '/dashboard/introducing-broker/register/type'

const RedirectToWithdrawal: React.FC = () => {
  const { walletId } = useParams<{ walletId?: string }>()
  const location = useLocation()
  return (
    <Navigate
      to='/dashboard/introducing-broker/withdrawal/wallet'
      state={{ ...location.state, selectedWalletId: walletId || location.state?.selectedWalletId }}
      replace
    />
  )
}

export const useIBRoutes = (): RouteConfig[] => {
  const visible = useIBRoutesConditions()
  const { t } = useTranslation()
  const isDashboardPoCActive = true
  const { account } = useAccountReadContext()
  const { data: introducingBroker } = useIntroducingBrokerById()

  const multiTierChildren = useMemo(() => {
    return [
      {
        path: 'commission-plans',
        element: <CommissionPlansPage />,
        ...(introducingBroker?.isLastMultiTierLevel === false
          ? { meta: { breadcrumbLabel: t('IB.MultiTier.IB Commission Plans') } }
          : {}),
      },
      ...(introducingBroker?.isLastMultiTierLevel === false
        ? [
            {
              path: 'sub-ib-accounts',
              element: <SubIBAccountsPage />,
              meta: { breadcrumbLabel: t('IB.MultiTier.Sub-IB Accounts') },
            },
            {
              path: 'multi-tier-network',
              element: <MultiTierNetworkPage />,
              meta: { breadcrumbLabel: t('IB.MultiTier.Multi-tier Network') },
            },
          ]
        : []),
    ]
  }, [introducingBroker?.isLastMultiTierLevel, t])

  const rootLabel = isTickmillPartner(account) ? t('Tabs.Partners') : t('Tabs.Introducing broker')

  return useMemo((): RouteConfig[] => {
    //@TODO: maybe we will need redefine routing
    const bypassRoutes = [
      {
        path: 'introducing-broker/register/:subCategoryId/questionnaire',
        element: (
          <IBQuestionnairePageGuard isMultiTier={visible.isIBQuestionnaireVisibleWithMultiTier} />
        ),
        condition: visible.isSignupVisible || visible.isIBQuestionnaireVisibleWithMultiTier,
      },
      {
        path: 'introducing-broker/register/:subCategoryId/thank-you',
        element: <IBSignupThankYouPageGuard />,
        condition: visible.isIBVisible,
      },
    ]

    return [
      ...bypassRoutes,
      {
        path: 'introducing-broker',
        outlet: <IntroducingBroker />,
        condition: visible.isSignupVisible,
        meta: { breadcrumbLabel: rootLabel },
        children: [
          {
            path: 'register',
            outlet: <IBSignUpPage />,
            meta: {
              hideChildrenBreadcrumbs: true,
              breadcrumbLabel: '',
            },
            children: [
              {
                path: 'type',
                element: <IBSignUpTypePage />,
              },
              {
                path: ':subCategoryId',
                element: <Navigate to={IB_DEFAULT_REGISTER_PAGE_PATH} />,
              },
              {
                path: '*',
                element: <Navigate to={IB_DEFAULT_REGISTER_PAGE_PATH} />,
              },
            ],
          },
          {
            path: '*',
            element: <Navigate to={IB_DEFAULT_REGISTER_PAGE_PATH} />,
          },
        ],
      },
      {
        path: 'introducing-broker',
        outlet: <IntroducingBroker />,
        condition: visible.isIBVisible && !visible.isSignupVisible,
        meta: { breadcrumbLabel: rootLabel },
        children: [
          {
            path: 'dashboard',
            element: <IBDashboardPage />,
            outlet: <DashboardWrapper />,
            condition: isDashboardPoCActive,
            meta: { breadcrumbLabel: t('Tabs.Dashboard') },
            children: [
              {
                path: 'edit/:layoutId',
                element: <IBDashboardEditPage />,
                layout: 'full-no-menu',
              },
            ],
          },
          {
            path: 'rebates',
            outlet: <IBRebatesPage />,
            element: <Navigate to='clients' />,
            condition: visible.isRebatesVisible,
            meta: { breadcrumbLabel: t('Tabs.Rebates') },
            children: [
              {
                path: 'clients',
                element: <IBRebatesClientsPage />,
                meta: { breadcrumbLabel: t('IB.RebatesPage.Clients') },
              },
              {
                path: 'approvals',
                element: <IBRebateApprovalsPage />,
                meta: { breadcrumbLabel: t('IB.RebatesPage.Approvals') },
              },
              {
                path: 'groups',
                element: <IBRebatesGroupsPage />,
                meta: { breadcrumbLabel: t('IB.RebatesPage.Groups') },
              },
              {
                path: 'transactions',
                element: <IBRebatesTransactionsPage />,
                meta: { breadcrumbLabel: t('IB.RebatesPage.Transactions') },
              },
            ],
          },
          {
            path: 'wallets',
            element: <IBWalletsPage />,
            meta: { breadcrumbLabel: t('Tabs.Wallets'), hideChildrenBreadcrumbs: true },
            children: [
              {
                path: ':walletId',
                children: [
                  {
                    path: 'withdrawal',
                    element: <RedirectToWithdrawal />,
                  },
                  {
                    path: 'transfer',
                    element: <IBWalletTransferPage />,
                  },
                ],
              },
            ],
          },
          {
            path: 'withdrawal',
            element: <RedirectToWithdrawal />,
            layout: 'full-no-menu',
            children: [
              {
                path: 'wallet',
                element: <WithdrawalPage dashboardContext='introducing-broker' />,
              },
              {
                path: 'wallet/:id/:pspId',
                outlet: (
                  <WithdrawalFlowLayoutPage target='wallet' dashboardContext='introducing-broker' />
                ),
                element: <Navigate to='amount' replace />,
                children: [
                  { path: 'amount', element: <WithdrawalAmountPage /> },
                  { path: 'two-factor', element: <WithdrawalTwoFactorPage /> },
                  { path: 'transaction/:status', element: <WithdrawalTransactionPage /> },
                ],
              },
            ],
          },
          {
            path: 'reports',
            outlet: <ReportsPage />,
            element: <Navigate to='clients' />,
            meta: { breadcrumbLabel: t('Tabs.Reports') },
            children: [
              {
                path: 'clients',
                element: (
                  <DateFilterContextProvider>
                    <IBClientsPage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('IB.Clients') },
              },
              {
                path: 'income',
                element: (
                  <DateFilterContextProvider>
                    <IBIncomePage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('IB.Income') },
              },
              {
                path: 'ib-reports',
                element: (
                  <DateFilterContextProvider>
                    <IBReportsPage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('Tabs.IB Reports') },
              },
              {
                path: 'cjm',
                element: (
                  <DateFilterContextProvider>
                    <IBClientJourneyMapPage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('Tabs.Client Journey Map') },
              },
              {
                path: 'transactions-history',
                element: (
                  <DateFilterContextProvider>
                    <IBTransactionHistoryPage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('IB.Transactions History') },
              },
              {
                path: 'export-hub',
                element: (
                  <DateFilterContextProvider>
                    <ExportHubPage />
                  </DateFilterContextProvider>
                ),
                meta: { breadcrumbLabel: t('Tabs.Export Hub') },
              },
            ],
          },
          {
            path: 'legal-documents',
            element: <LegalDocumentsPage />,
            meta: { breadcrumbLabel: t('Tabs.Legal Documents') },
          },
          {
            path: 'ib-contest',
            element: (
              <DateFilterContextProvider>
                <IBContestPage />
              </DateFilterContextProvider>
            ),
            meta: { breadcrumbLabel: t('Tabs.IB Contest') },
          },
          {
            path: 'bonus-program',
            element: <LoyaltyPage />,
            meta: { breadcrumbLabel: t('Loyalty Program.Bonus program') },
          },
          {
            path: 'referral-materials',
            element: <ReferralMaterialsPage />,
            condition: visible.isReferralMaterialsVisible,
            meta: { breadcrumbLabel: t('Tabs.Referral Materials') },
          },
          {
            path: 'multi-tier',
            outlet: <MultiTierPage />,
            element: <Navigate to='commission-plans' />,
            condition: visible.isMultiTierVisible,
            meta: { breadcrumbLabel: t('Tabs.Multi-Tier') },
            children: multiTierChildren,
          },
          {
            path: 'link-hub',
            element: <LinkHubPage />,
            meta: { breadcrumbLabel: t('Tabs.Link Hub') },
            condition: visible.isLinkHubVisible,
          },
          {
            path: '*',
            element: <Navigate to={CommonPaths.IntroducingBrokerDashboard} />,
          },
        ],
      },
    ]
  }, [
    visible.isIBQuestionnaireVisibleWithMultiTier,
    visible.isSignupVisible,
    visible.isIBVisible,
    visible.isRebatesVisible,
    visible.isReferralMaterialsVisible,
    visible.isMultiTierVisible,
    visible.isLinkHubVisible,
    rootLabel,
    t,
    isDashboardPoCActive,
    multiTierChildren,
  ])
}

interface RoutesConditions {
  isIBVisible: boolean
  isMultiTierVisible: boolean
  isSignupVisible: boolean
  isReferralMaterialsVisible: boolean
  isIBQuestionnaireVisibleWithMultiTier: boolean
  isRebatesVisible: boolean
  isLastMultiTierLevel?: boolean
  isLinkHubVisible: boolean
}

export const useIBRoutesConditions = (): RoutesConditions => {
  const { account } = useAccountReadContext()
  const { multiTierProgram } = useIBPrograms()
  const { data: introducingBroker } = useIntroducingBrokerById()

  //@TODO: later on we need to update this condition by agreement with BE (when it will be added to account visibilityConfiguration as showIbRebates)
  //temporary hidden due special request for IBs under PAs
  //https://tickmill.atlassian.net/browse/WCA-5760
  const isRebatesVisible = useMemo(
    () => !(account?.clientIntroducingBroker?.id && isTickmillPartner(account)),
    [account]
  )

  const isIBVisible = useMemo(() => !!account?.visibilityConfiguration.hasIBRoom, [account])

  const isMultiTierVisible = useMemo(
    () => !!account?.visibilityConfiguration.hasMultiTier && multiTierProgram,
    [account?.visibilityConfiguration.hasMultiTier, multiTierProgram]
  )

  const isSignupVisible = useMemo(
    () => !account?.clientIntroducingBroker?.id && !!account?.visibilityConfiguration.hasIBRoom,
    [account]
  )

  const isReferralMaterialsVisible = useMemo(() => !isTickmillPartner(account), [account])

  const isLastMultiTierLevel = introducingBroker?.isLastMultiTierLevel

  const isLinkHubVisible = useMemo(
    () => !isTickmillPartner(account) && !isTickmillAS(account),
    [account]
  )

  const isIBQuestionnaireVisibleWithMultiTier =
    isIBVisible && !isSignupVisible && isMultiTierVisible

  return {
    isIBVisible,
    isSignupVisible,
    isMultiTierVisible,
    isReferralMaterialsVisible,
    isIBQuestionnaireVisibleWithMultiTier,
    isRebatesVisible,
    isLastMultiTierLevel,
    isLinkHubVisible,
  }
}
